import type { ProductItem } from '@/lib/api/products';
import type { SearchProductOutputItem } from '@/lib/api/products';

export interface SetupCheckoutLineItem {
  variantId: string;
  priceId: string;
  quantity: number;
  productId?: string;
  productName?: string;
}

function pickPriceForCurrency(
  prices: NonNullable<ProductItem['variants']>[number]['prices'],
  currency: string,
) {
  if (!prices?.length) return null;

  const withCurrency = prices.filter((entry) => entry.currencyOptions?.[currency]);
  if (!withCurrency.length) return null;

  return withCurrency.find((entry) => entry.default) ?? withCurrency[0];
}

/** Pick the first sellable variant + price for checkout/init (matches DirectLinkDialog shape). */
export function pickFirstCheckoutLineItem(
  products: ProductItem[] | undefined,
  currency: string,
): SetupCheckoutLineItem | null {
  if (!products?.length) return null;

  for (const product of products) {
    if (product.active === false) continue;

    const variants = product.variants ?? [];
    const variant = variants.find((v) => v.default) ?? variants[0];

    if (!variant?.id) continue;

    const price = pickPriceForCurrency(variant.prices, currency);
    if (!price?.id) continue;

    return {
      variantId: variant.id,
      priceId: price.id,
      quantity: 1,
      productId: product.id,
      productName: product.name,
    };
  }

  return null;
}

export function lineItemFromProductItem(
  product: ProductItem,
  currency: string,
): SetupCheckoutLineItem | null {
  if (product.active === false) return null;

  const variants = product.variants ?? [];
  const variant = variants.find((entry) => entry.default) ?? variants[0];
  if (!variant?.id) return null;

  const price = pickPriceForCurrency(variant.prices, currency);
  if (!price?.id) return null;

  return {
    variantId: variant.id,
    priceId: price.id,
    quantity: 1,
    productId: product.id,
    productName: product.name,
  };
}

export interface SetupCheckoutProductOption {
  productId: string;
  name: string;
  imageUrl: string;
  priceLabel: string;
  lineItem: SetupCheckoutLineItem;
}

function formatCheckoutPrice(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amountMinor / 100);
}

export function buildCheckoutProductOptions(
  products: ProductItem[] | undefined,
  currency: string,
): SetupCheckoutProductOption[] {
  if (!products?.length) return [];

  const options: SetupCheckoutProductOption[] = [];

  for (const product of products) {
    const lineItem = lineItemFromProductItem(product, currency);
    if (!lineItem) continue;

    const variant = product.variants?.find((entry) => entry.id === lineItem.variantId);
    const price = variant?.prices?.find((entry) => entry.id === lineItem.priceId);
    const amountMinor = price?.currencyOptions?.[currency]?.amount;

    options.push({
      productId: product.id,
      name: product.name?.trim() || lineItem.productName || '',
      imageUrl: variant?.imageUrl?.trim() || product.image?.trim() || '',
      priceLabel: amountMinor != null && amountMinor > 0 ? formatCheckoutPrice(amountMinor, currency) : '',
      lineItem,
    });
  }

  return options;
}

export function lineItemFromSearchProduct(
  product: SearchProductOutputItem,
  currency: string,
): SetupCheckoutLineItem | null {
  const variant = product.variants?.find((v) => v.default) ?? product.variants?.[0];
  if (!variant?.id) return null;

  const price = pickPriceForCurrency(variant.prices, currency);
  if (!price?.id) return null;

  return {
    variantId: variant.id,
    priceId: price.id,
    quantity: 1,
    productId: product.id,
    productName: product.name,
  };
}
